import { db } from '../firebase';
import { doc, getDoc, updateDoc, collection, addDoc, Timestamp } from 'firebase/firestore';
import { recordTemplateUsage as recordMetricsTemplateUsage } from './metricsService';

/**
 * Structure of templateUsage in user document:
 * {
 *   templateUsage: {
 *     product: number,
 *     variant: number,
 *     templates: {
 *       [templateId]: {
 *         lastUsed: timestamp,
 *         type: string,
 *         useCount: number
 *       }
 *     }
 *   }
 * }
 */

// Initialize template usage tracking for a user
export const initializeTemplateUsage = async (userId) => {
  const userRef = doc(db, 'users', userId);
  const userDoc = await getDoc(userRef);

  if (!userDoc.exists() || !userDoc.data().templateUsage) {
    await updateDoc(userRef, {
      templateUsage: {
        product: 0,
        variant: 0,
        templates: {}
      }
    });
  }
};

// Track template usage
export const trackTemplateUsage = async (userId, templateId, templateType, shopId) => {
  if (!userId || !templateId || !shopId) {
    console.error('Missing required parameters:', { userId, templateId, shopId });
    return;
  }

  try {
    console.log('Starting trackTemplateUsage with params:', { userId, templateId, templateType, shopId });
    
    // Record the usage in templateStats collection
    const usage = {
      userId,
      templateId,
      shopId,
      templateType,
      timestamp: Timestamp.now(),
      success: true // We'll assume success at this point
    };

    // Add to templateStats collection
    console.log('Adding usage record to templateStats:', usage);
    await addDoc(collection(db, 'templateStats'), usage);
    console.log('Successfully added usage record to templateStats');

    // Update metrics in shop document
    console.log('Updating metrics in shop document');
    await recordMetricsTemplateUsage(userId, shopId, templateId, true);
    console.log('Successfully updated metrics in shop document');

    console.log('Template usage recorded successfully:', usage);
  } catch (error) {
    console.error('Error recording template usage:', error);
    
    // Still try to record the failed attempt
    try {
      console.log('Attempting to record failed template usage');
      await recordMetricsTemplateUsage(userId, shopId, templateId, false);
      console.log('Successfully recorded failed template usage');
    } catch (innerError) {
      console.error('Error recording failed template usage:', innerError);
    }
  }
};

// Export the recordTemplateUsage function for backward compatibility
export const recordTemplateUsage = async (userId, templateId, shopId) => {
  console.log('Using recordTemplateUsage with params:', { userId, templateId, shopId });
  
  try {
    // Use a default template type since we might not have access to the template object
    const templateType = 'product';
    
    // Call the trackTemplateUsage function with the parameters
    return trackTemplateUsage(userId, templateId, templateType, shopId);
  } catch (error) {
    console.error('Error in recordTemplateUsage:', error);
    // Don't throw the error to maintain backward compatibility
  }
};

// Get template usage stats for a user
export const getTemplateUsageStats = async (userId) => {
  const userRef = doc(db, 'users', userId);
  const userDoc = await getDoc(userRef);
  
  const usage = userDoc.data()?.templateUsage || {
    product: 0,
    variant: 0,
    templates: {}
  };
  
  return {
    product: usage.product || 0,
    variant: usage.variant || 0,
    templates: usage.templates || {}
  };
};
